// piano-frontend/src/components/UnassignedResultDialog.js
// 고정제 등록/수정/재생성 결과 다이얼로그 — 자리가 없어 미배정(스킵)된 날짜, 발행/회수된 보강 티켓,
// 보존된 수동 예약 등을 명시하고 "지금 보강 레슨 잡기"로 이어준다.
import React from "react";
import {
  Box, Typography, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Stack, Alert, Divider
} from "@mui/material";
import Dialog from "./AppDialog";
import { CheckCircle, EventBusy } from "@mui/icons-material";

const KO_DAYS = ["일", "월", "화", "수", "목", "금", "토"];
const REASON_LABEL = {
  SLOT_FULL: { label: "기존 예약 있음", color: "warning" },
  BLOCKED: { label: "블록", color: "default" },
  ROOM_FULL: { label: "레슨실 없음", color: "error" },
  HOLIDAY: { label: "휴일", color: "default" },
};

function fmtDate(dateStr) {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()} (${KO_DAYS[d.getDay()]})`;
}

function SkipRow({ skip, teacherName }) {
  const reason = REASON_LABEL[skip.reason] || { label: skip.reason, color: "default" };
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
      <EventBusy fontSize="small" color="disabled" />
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 130 }}>
        {fmtDate(skip.date)} {String(skip.start_time).slice(0, 5)}
      </Typography>
      {teacherName && (
        <Typography variant="body2" color="text.secondary">{teacherName}</Typography>
      )}
      <Chip size="small" label={reason.label} color={reason.color} variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
      {skip.occupant_names && skip.occupant_names.length > 0 && (
        <Typography variant="caption" color="text.secondary" noWrap>
          점유: {Array.isArray(skip.occupant_names) ? skip.occupant_names.join(", ") : skip.occupant_names}
        </Typography>
      )}
    </Stack>
  );
}

// sequence = { current, total }: 플래너 일괄 변경처럼 여러 회원의 결과를 차례로 보여줄 때,
// 관리자가 "총 몇 명을 확인/보강 잡기 해야 하는지" 알 수 있게 표기한다.
function UnassignedResultDialog({ open, onClose, title, memberName, result, teacherOptions = [], onOpenMakeup, sequence = null }) {
  if (!result) return null;

  const teacherNameOf = (id) => teacherOptions.find((t) => t.id === id)?.name || "";
  const skips = result.skips || [];
  const confirmedSkips = skips.filter((s) => !s.is_provisional);
  const provisionalSkips = skips.filter((s) => s.is_provisional);
  const practiceWarnings = result.practiceWarnings || [];
  const unreclaimable = result.unreclaimable || [];
  const showMakeupButton = confirmedSkips.length > 0 || (result.ticketsIssued || 0) > 0;
  const multi = !!sequence && sequence.total > 1;
  const remaining = multi ? sequence.total - sequence.current : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircle color="success" />
        <Box component="span" sx={{ flex: 1 }}>{title || "처리 완료"}</Box>
        {multi && (
          <Chip size="small" color="primary" sx={{ fontWeight: 700 }} label={`회원 ${sequence.current} / ${sequence.total}`} />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {multi && (
            <Alert severity="info" icon={<span>👥</span>}>
              이번 일괄 변경으로 확인할 회원이 <b>총 {sequence.total}명</b>입니다 — 지금{" "}
              <b>{sequence.current}번째{memberName ? `: ${memberName}` : ""}</b> 회원.
              {remaining > 0 && (
                <> 이 창(또는 보강 잡기 창)을 닫으면 <b>다음 회원 결과가 이어서</b> 표시됩니다.</>
              )}
            </Alert>
          )}

          {confirmedSkips.length > 0 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 1 }}>
                아래 <b>{confirmedSkips.length}건</b>은 자리가 없어 자동 예약이 잡히지 않았습니다.
              </Alert>
              <Box sx={{ pl: 0.5 }}>
                {confirmedSkips.map((s, i) => (
                  <SkipRow key={i} skip={s} teacherName={teacherNameOf(s.teacher_id)} />
                ))}
              </Box>
            </Box>
          )}

          {(result.ticketsIssued || 0) > 0 && (
            <Alert severity="info" icon={<span>🎟️</span>}>
              미배정 {result.ticketsIssued}건만큼 <b>보강 티켓 {result.ticketsIssued}장</b>을 발행했습니다.
              회원과 협의한 일시에 <b>보강 레슨을 직접 잡아주세요.</b> 보강 티켓이 0장이 되면 모두 처리된 것입니다.
            </Alert>
          )}

          {(result.recoveredReclaimed ?? result.reclaimed ?? 0) > 0 && (
            <Alert severity="success" icon={<span>♻️</span>}>
              이전에 미배정됐던 슬롯이 다시 예약되어 보강 티켓{" "}
              <b>{result.recoveredReclaimed ?? result.reclaimed}장을 회수</b>했습니다.
            </Alert>
          )}

          {(result.movedResolved || 0) > 0 && (
            <Alert severity="success" icon={<span>♻️</span>}>
              슬롯이 변경되어 이전 미배정·취소 기록 <b>{result.movedResolved}건</b>을 새 스케줄로
              대체 처리했습니다
              {(result.movedReclaimed || 0) > 0 && (
                <>
                  {" "}(보강/환불 티켓 <b>{result.movedReclaimed}장 회수</b>)
                </>
              )}
              .
            </Alert>
          )}

          {(result.coveredRemoved || 0) > 0 && (
            <Alert severity="success" icon={<span>♻️</span>}>
              보강 티켓이 이미 사용된(보강 예약 완료) <b>{result.coveredRemoved}건</b>은 이중 수업이
              되지 않도록 해당 날짜의 레슨을 <b>생성하지 않았습니다</b>:{" "}
              {(result.coveredDates || []).map(fmtDate).join(", ")}
            </Alert>
          )}

          {unreclaimable.length > 0 && (
            <Alert severity="warning">
              보강 티켓이 이미 사용된 미배정 기록을 자동 상쇄하지 못했습니다: {unreclaimable.join(", ")}
              <br />초과 수업 가능성이 있으니 해당 주차 레슨 1건을 <b>티켓 반환 없이 취소</b>로
              정리해주세요.
            </Alert>
          )}

          {provisionalSkips.length > 0 && (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              미결제(2개월차) 구간 {provisionalSkips.length}건은 <b>결제 시점에 자동 재시도</b>됩니다.
              여전히 자리가 없으면 그때 보강 티켓이 발행됩니다.
            </Alert>
          )}

          {(result.preservedCount || 0) > 0 && (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              기존 수동 예약(보강 등) <b>{result.preservedCount}건은 그대로 보존</b>되었습니다.
            </Alert>
          )}

          {practiceWarnings.length > 0 && (
            <>
              <Divider />
              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                연습실 배정 실패 {practiceWarnings.length}건 (레슨은 정상 생성됨):{" "}
                {practiceWarnings.map((w) => `${fmtDate(w.date)}${w.time ? ` ${w.time}` : ""}`).join(", ")}
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {remaining > 0 ? `다음 회원 결과 보기 (${remaining}명 남음)` : "나중에"}
        </Button>
        {showMakeupButton && (
          <Button variant="contained" onClick={onOpenMakeup}>
            지금 보강 레슨 잡기
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UnassignedResultDialog;
